// @ts-check
// Grafo de arcos (flechas manuales) + validación de cardinalidades (TSK-0191,
// fase 1 de DEC-069 -- docs/generated/decisions.md#DEC-069).
//
// Contexto de arquitectura (DEC-068/DEC-069): hoy la conectividad del
// diagrama es IMPLÍCITA en el árbol IR (orden en `Secuencia`, `rama_si`/
// `rama_sino` de `Condicional`, `cuerpo` de `Bucle*`; ver `editor-model.js`/
// `connectors.js`). DEC-069 diseña, en fases, un grafo EXPLÍCITO de arcos
// dibujados por el alumno (`Arco = { id, desdeNodoId, desdePuerto,
// hastaNodoId, hastaPuerto }`) que más adelante (TSK-0193) compila a esa
// misma IR estructurada -- sin cambiar el contrato del backend. Este módulo
// es la fase 1: SOLO el modelo puro (puertos por tipo de nodo, operaciones
// inmutables sobre el grafo y `validarGrafoArcos`). No hay DOM, no hay
// `fetch`, no se toca `canvas-view.js`/`connectors.js`/`editor-model.js`
// (rutas prohibidas de la ficha de esta tarea): la única dependencia es
// `TIPO_NODO` de `editor-model.js`, reutilizado como referencia de los
// nombres de tipo de nodo (mismo criterio de reutilización que ya aplica
// `connectors.js`).
//
// Modelo de puertos (DEC-069 punto 1): `Inicio` y `Fin` no tienen nodo IR
// propio (igual que las pseudo-anclas `ID_CONTENEDOR_INICIO`/
// `ID_CONTENEDOR_FIN` de `connectors.js`, que este módulo NO importa por estar
// en rutas prohibidas: se redefine aquí el mismo convenio de forma
// autocontenida) -- se identifican con las claves literales `'inicio'`/
// `'fin'` en vez de un `NodoModelo`. Por eso `puertosDeNodo` y las entradas de
// `nodos` en `validarGrafoArcos` aceptan `NodoModelo | 'inicio' | 'fin'`.
//
// Ambigüedad resuelta (repórtese también en la respuesta final del agente
// ejecutor): DEC-069 punto 3 solo exige cardinalidad ≤1/=1 LOCAL por nodo;
// la convergencia de las dos ramas de un `Condicional` en un punto posterior
// común (post-dominador) es un análisis de FLUJO GLOBAL que pertenece al
// compilador grafo→IR (TSK-0193, todavía no implementado), no a esta
// validación local. En consecuencia, un grafo puede ser válido según
// `validarGrafoArcos` (todas las reglas locales de DEC-068 punto 3 se
// cumplen) sin que todos los caminos alcancen `Fin`: esa comprobación de
// alcanzabilidad/estructura completa se difiere a TSK-0193 explícitamente.
//
// Otra ambigüedad resuelta: el `SALIDA` de un `Bucle*` (continuación tras el
// bucle) se modela con la misma cota `≤1` que un bloque normal (DEC-069 no
// fija un mínimo para esa salida, a diferencia de `CUERPO`, que si es
// obligatorio "exactamente 1"), coherente con que la edición nunca se
// bloquea (DEC-069 punto 3: un grafo temporalmente incompleto es válido de
// mostrar, solo se avisa).

import { TIPO_NODO } from './editor-model.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 */

/**
 * Valor de puerto: los mismos 5 posibles para cualquier tipo de nodo
 * (DEC-069 punto 1).
 * @typedef {'in'|'out'|'true'|'false'|'body'} ValorPuerto
 */

/**
 * Un nodo del grafo de arcos: o bien un bloque real del modelo lógico
 * (`NodoModelo`, con su discriminador `nodo`), o bien una de las dos
 * pseudo-anclas sin nodo IR propio, `'inicio'`/`'fin'`.
 * @typedef {NodoModelo | 'inicio' | 'fin'} NodoOClave
 */

/**
 * @typedef {{
 *   id: string,
 *   desdeNodoId: string,
 *   desdePuerto: ValorPuerto,
 *   hastaNodoId: string,
 *   hastaPuerto: ValorPuerto,
 * }} Arco
 */

/** @typedef {{ arcos: Arco[] }} GrafoArcos */

/** @typedef {{ entrada: boolean, salidas: ValorPuerto[] }} PuertosNodo */

/**
 * Una violación de `validarGrafoArcos`: `tipo` es un código estable (para
 * que la UI futura -- TSK-0194 -- pueda mapear cada uno a un mensaje/estilo),
 * `mensaje` es ya legible para mostrar directamente.
 * @typedef {{ tipo: string, nodoId?: string, arcoId?: string, mensaje: string }} ViolacionGrafo
 */

// --------------------------------------------------------------------------
// Puertos
// --------------------------------------------------------------------------

/** Enum de puertos (DEC-069 punto 1). Mismo idioma `Object.freeze` que
 * `TIPO_NODO`/`OPERADOR` en `editor-model.js`. */
export const PUERTO = Object.freeze({
  ENTRADA: /** @type {ValorPuerto} */ ('in'),
  SALIDA: /** @type {ValorPuerto} */ ('out'),
  VERDADERO: /** @type {ValorPuerto} */ ('true'),
  FALSO: /** @type {ValorPuerto} */ ('false'),
  CUERPO: /** @type {ValorPuerto} */ ('body'),
});

const VALORES_PUERTO = /** @type {Set<string>} */ (new Set(Object.values(PUERTO)));

/** Nodos de bucle: mismo conjunto que `NODOS_BUCLE` de `connectors.js`,
 * redefinido aquí (no se importa: `connectors.js` es ruta prohibida). */
const NODOS_BUCLE = /** @type {Set<string>} */ (
  new Set([TIPO_NODO.BUCLE_MIENTRAS, TIPO_NODO.BUCLE_FIN_PARA, TIPO_NODO.REPETIR_HASTA])
);

/** Pseudo-ancla "Inicio": mismo valor literal que `ID_CONTENEDOR_INICIO` de
 * `connectors.js`, redefinido aquí de forma autocontenida (ver cabecera). */
const CLAVE_INICIO = 'inicio';
/** Pseudo-ancla "Fin": ver `CLAVE_INICIO`. */
const CLAVE_FIN = 'fin';

/**
 * Tabla de puertos por tipo de nodo (DEC-069 punto 1): `Inicio` = solo
 * `SALIDA`; `Fin` = solo `ENTRADA`; bloque normal (Entrada/Salida/
 * Asignacion/Operacion/Llamada/Retorno) = 1 `ENTRADA` + 1 `SALIDA`;
 * `Condicional` = 1 `ENTRADA` + salidas `VERDADERO`/`FALSO` (sin `SALIDA`
 * ordinaria); `Bucle*` = 1 `ENTRADA` + `CUERPO` (entrada al cuerpo) +
 * `SALIDA` (continuación tras el bucle); `Comentario` no participa del flujo
 * (sin puertos).
 * @type {Map<string, PuertosNodo>}
 */
const PUERTOS_POR_TIPO = new Map([
  [CLAVE_INICIO, { entrada: false, salidas: [PUERTO.SALIDA] }],
  [CLAVE_FIN, { entrada: true, salidas: [] }],
  [TIPO_NODO.CONDICIONAL, { entrada: true, salidas: [PUERTO.VERDADERO, PUERTO.FALSO] }],
  [TIPO_NODO.BUCLE_MIENTRAS, { entrada: true, salidas: [PUERTO.CUERPO, PUERTO.SALIDA] }],
  [TIPO_NODO.BUCLE_FIN_PARA, { entrada: true, salidas: [PUERTO.CUERPO, PUERTO.SALIDA] }],
  [TIPO_NODO.REPETIR_HASTA, { entrada: true, salidas: [PUERTO.CUERPO, PUERTO.SALIDA] }],
  [TIPO_NODO.COMENTARIO, { entrada: false, salidas: [] }],
  [TIPO_NODO.ENTRADA, { entrada: true, salidas: [PUERTO.SALIDA] }],
  [TIPO_NODO.SALIDA, { entrada: true, salidas: [PUERTO.SALIDA] }],
  [TIPO_NODO.ASIGNACION, { entrada: true, salidas: [PUERTO.SALIDA] }],
  [TIPO_NODO.OPERACION, { entrada: true, salidas: [PUERTO.SALIDA] }],
  [TIPO_NODO.LLAMADA, { entrada: true, salidas: [PUERTO.SALIDA] }],
  [TIPO_NODO.RETORNO, { entrada: true, salidas: [PUERTO.SALIDA] }],
]);

/**
 * @param {NodoOClave} nodoOClave
 * @returns {string}
 */
function claveDe(nodoOClave) {
  if (typeof nodoOClave === 'string') return nodoOClave;
  if (
    typeof nodoOClave !== 'object' ||
    nodoOClave === null ||
    typeof (/** @type {Record<string, unknown>} */ (nodoOClave).nodo) !== 'string'
  ) {
    throw new TypeError(
      '"nodoOClave" debe ser "inicio", "fin" o un NodoModelo con campo "nodo" de cadena'
    );
  }
  return /** @type {string} */ (nodoOClave.nodo);
}

/**
 * @param {NodoOClave} nodoOClave
 * @returns {string}
 */
function idDe(nodoOClave) {
  return typeof nodoOClave === 'string' ? nodoOClave : nodoOClave.id;
}

/**
 * Devuelve el modelo de puertos -- `{ entrada, salidas }` -- de un tipo de
 * nodo (DEC-069 punto 1). Acepta directamente un `NodoModelo` (usa su campo
 * `nodo`) o las pseudo-anclas `'inicio'`/`'fin'`. Devuelve siempre una copia
 * (nunca la entrada de la tabla interna) para que el llamador no pueda mutar
 * el modelo de puertos de este módulo.
 * @param {NodoOClave} nodoOClave
 * @returns {PuertosNodo}
 */
export function puertosDeNodo(nodoOClave) {
  const clave = claveDe(nodoOClave);
  const puertos = PUERTOS_POR_TIPO.get(clave);
  if (!puertos) {
    throw new TypeError(`tipo de nodo desconocido para el modelo de puertos: '${clave}'`);
  }
  return { entrada: puertos.entrada, salidas: puertos.salidas.slice() };
}

// --------------------------------------------------------------------------
// Validación mínima de argumentos (errores explícitos, mismo idioma que
// `editor-model.js`/`connectors.js`)
// --------------------------------------------------------------------------

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {string}
 */
function requerirCadena(valor, nombreCampo) {
  if (typeof valor !== 'string' || valor.length === 0) {
    throw new TypeError(`${nombreCampo} debe ser una cadena no vacía`);
  }
  return valor;
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {ValorPuerto}
 */
function requerirPuerto(valor, nombreCampo) {
  if (typeof valor !== 'string' || !VALORES_PUERTO.has(valor)) {
    throw new TypeError(`${nombreCampo} debe ser un valor de PUERTO válido, recibió '${String(valor)}'`);
  }
  return /** @type {ValorPuerto} */ (valor);
}

/**
 * @param {unknown} valor
 * @returns {valor is Arco}
 */
function esArco(valor) {
  if (typeof valor !== 'object' || valor === null) return false;
  const candidato = /** @type {Record<string, unknown>} */ (valor);
  return (
    typeof candidato.id === 'string' &&
    typeof candidato.desdeNodoId === 'string' &&
    typeof candidato.hastaNodoId === 'string' &&
    typeof candidato.desdePuerto === 'string' &&
    VALORES_PUERTO.has(candidato.desdePuerto) &&
    typeof candidato.hastaPuerto === 'string' &&
    VALORES_PUERTO.has(candidato.hastaPuerto)
  );
}

/**
 * @param {unknown} valor
 * @returns {Arco}
 */
function requerirArco(valor) {
  if (!esArco(valor)) {
    throw new TypeError(
      '"arco" debe tener la forma { id, desdeNodoId, desdePuerto, hastaNodoId, hastaPuerto } con puertos válidos'
    );
  }
  return valor;
}

/**
 * @param {unknown} valor
 * @returns {GrafoArcos}
 */
function requerirGrafoArcos(valor) {
  if (
    typeof valor !== 'object' ||
    valor === null ||
    !Array.isArray(/** @type {Record<string, unknown>} */ (valor).arcos)
  ) {
    throw new TypeError('"grafo" debe ser un grafo de arcos válido ({ arcos: Arco[] })');
  }
  return /** @type {GrafoArcos} */ (valor);
}

// --------------------------------------------------------------------------
// Factoría de arco (id auto-generado, mismo idioma que `generarId` de
// `editor-model.js`, pero encapsulado en este módulo: `editor-model.js` es
// ruta prohibida para edición y no expone su generador)
// --------------------------------------------------------------------------

let contadorArco = 0;

/**
 * Construye un `Arco` nuevo con un `id` único autogenerado. No lo inserta en
 * ningún grafo (usar `agregarArco` para eso): separa "crear el dato" de
 * "mutar -de forma inmutable- el grafo", mismo criterio que las factorías
 * `crear*` de `editor-model.js` frente a `insertarEnSecuencia`.
 * @param {{ desdeNodoId: string, desdePuerto: ValorPuerto, hastaNodoId: string, hastaPuerto: ValorPuerto }} campos
 * @returns {Arco}
 */
export function crearArco({ desdeNodoId, desdePuerto, hastaNodoId, hastaPuerto }) {
  contadorArco += 1;
  return {
    id: `Arco-${contadorArco}`,
    desdeNodoId: requerirCadena(desdeNodoId, 'desdeNodoId'),
    desdePuerto: requerirPuerto(desdePuerto, 'desdePuerto'),
    hastaNodoId: requerirCadena(hastaNodoId, 'hastaNodoId'),
    hastaPuerto: requerirPuerto(hastaPuerto, 'hastaPuerto'),
  };
}

// --------------------------------------------------------------------------
// Operaciones inmutables sobre el grafo (ninguna muta el grafo recibido)
// --------------------------------------------------------------------------

/**
 * @returns {GrafoArcos}
 */
export function crearGrafoArcos() {
  return { arcos: [] };
}

/**
 * Añade `arco` de forma inmutable. Lanza si `arco` no tiene forma válida o
 * si ya existe un arco con el mismo `id` en `grafo` (mismo idioma de
 * duplicado que `conectarBloque` de `connectors.js`).
 * @param {GrafoArcos} grafo
 * @param {Arco} arco
 * @returns {GrafoArcos}
 */
export function agregarArco(grafo, arco) {
  requerirGrafoArcos(grafo);
  requerirArco(arco);
  if (grafo.arcos.some((existente) => existente.id === arco.id)) {
    throw new TypeError(`ya existe un arco con id '${arco.id}' en el grafo`);
  }
  return { arcos: [...grafo.arcos, arco] };
}

/**
 * Elimina de forma inmutable el arco `arcoId`. Si no existe, devuelve
 * `grafo` sin cambios (misma referencia), mismo idioma de "no encontrado"
 * que `eliminarFuncion`/`actualizarCuerpoFuncion` de `editor-model.js`.
 * @param {GrafoArcos} grafo
 * @param {string} arcoId
 * @returns {GrafoArcos}
 */
export function eliminarArco(grafo, arcoId) {
  requerirGrafoArcos(grafo);
  if (!grafo.arcos.some((arco) => arco.id === arcoId)) return grafo;
  return { arcos: grafo.arcos.filter((arco) => arco.id !== arcoId) };
}

/**
 * Mueve, de forma inmutable, uno de los dos extremos de un arco ya existente
 * a otro nodo/puerto (re-enganchar una flecha, DEC-069 punto 7: "reordenar"
 * un bloque es re-enganchar el arco que llega/sale de él, no una acción
 * aparte).
 * @param {GrafoArcos} grafo
 * @param {string} arcoId
 * @param {'desde'|'hasta'} extremo
 * @param {string} nuevoNodoId
 * @param {ValorPuerto} nuevoPuerto
 * @returns {GrafoArcos}
 */
export function reconectarExtremo(grafo, arcoId, extremo, nuevoNodoId, nuevoPuerto) {
  requerirGrafoArcos(grafo);
  if (extremo !== 'desde' && extremo !== 'hasta') {
    throw new TypeError(`"extremo" debe ser 'desde' o 'hasta', recibió '${String(extremo)}'`);
  }
  requerirCadena(nuevoNodoId, 'nuevoNodoId');
  requerirPuerto(nuevoPuerto, 'nuevoPuerto');

  const indice = grafo.arcos.findIndex((arco) => arco.id === arcoId);
  if (indice === -1) {
    throw new RangeError(`no existe un arco con id '${arcoId}' en el grafo`);
  }

  const arcoActual = grafo.arcos[indice];
  const arcoActualizado =
    extremo === 'desde'
      ? { ...arcoActual, desdeNodoId: nuevoNodoId, desdePuerto: nuevoPuerto }
      : { ...arcoActual, hastaNodoId: nuevoNodoId, hastaPuerto: nuevoPuerto };

  const arcos = grafo.arcos.slice();
  arcos[indice] = arcoActualizado;
  return { arcos };
}

/**
 * Arcos que SALEN de `nodoId`, opcionalmente filtrados por `desdePuerto`.
 * @param {GrafoArcos} grafo
 * @param {string} nodoId
 * @param {ValorPuerto} [puerto]
 * @returns {Arco[]}
 */
export function arcosDesde(grafo, nodoId, puerto) {
  requerirGrafoArcos(grafo);
  return grafo.arcos.filter(
    (arco) => arco.desdeNodoId === nodoId && (puerto === undefined || arco.desdePuerto === puerto)
  );
}

/**
 * Arcos que LLEGAN a `nodoId`, opcionalmente filtrados por `hastaPuerto`.
 * @param {GrafoArcos} grafo
 * @param {string} nodoId
 * @param {ValorPuerto} [puerto]
 * @returns {Arco[]}
 */
export function arcosHacia(grafo, nodoId, puerto) {
  requerirGrafoArcos(grafo);
  return grafo.arcos.filter(
    (arco) => arco.hastaNodoId === nodoId && (puerto === undefined || arco.hastaPuerto === puerto)
  );
}

// --------------------------------------------------------------------------
// Validación (DEC-068 punto 3 / DEC-069 punto 2)
// --------------------------------------------------------------------------

/**
 * @param {ViolacionGrafo[]} violaciones
 * @param {ViolacionGrafo} violacion
 */
function registrar(violaciones, violacion) {
  violaciones.push(violacion);
}

/**
 * Valida `grafo` contra `nodos` (la lista plana de bloques del diagrama, más
 * las pseudo-anclas `'inicio'`/`'fin'` si participan) y devuelve TODAS las
 * violaciones encontradas, sin abortar en la primera (DEC-069 punto 3: la
 * edición nunca se bloquea, se acumulan los problemas para mostrarlos
 * juntos). Un grafo válido devuelve `[]`.
 *
 * Cubre (DEC-068 punto 3):
 * - compatibilidad de puertos: ningún arco entra a Inicio (`INICIO_CON_ENTRADA`)
 *   ni sale de Fin (`FIN_CON_SALIDA`); cualquier otro puerto usado que no sea
 *   válido para el tipo de nodo de ese extremo (`PUERTO_INCOMPATIBLE`);
 *   extremos que referencian un nodo ausente de `nodos` (`NODO_DESCONOCIDO`).
 * - cardinalidad: bloque normal ≤1 entrada/≤1 salida (`MULTIPLES_ENTRADAS`/
 *   `MULTIPLES_SALIDAS`); `Condicional` exactamente 1 `VERDADERO` y 1 `FALSO`
 *   (`CONDICIONAL_RAMA_FALTANTE`/`CONDICIONAL_RAMA_DUPLICADA`, además de
 *   ≤1 entrada); `Inicio` exactamente 1 salida (`INICIO_SIN_SALIDA`/
 *   `MULTIPLES_SALIDAS`); `Fin` exactamente 1 entrada (`FIN_SIN_ENTRADA`/
 *   `MULTIPLES_ENTRADAS`); `Bucle*` exactamente 1 `CUERPO`
 *   (`BUCLE_SIN_CUERPO`/`BUCLE_CUERPO_DUPLICADO`, además de ≤1 entrada y ≤1
 *   salida ordinaria).
 * - aciclicidad (`CICLO`): el grafo dibujado por el alumno debe ser
 *   acíclico -- la repetición de un bucle se expresa por el puerto `CUERPO`,
 *   nunca por un arco de retorno dibujado (DEC-069 punto 1/2).
 *
 * No valida alcanzabilidad global de `Fin` ni convergencia de ramas
 * (post-dominador): eso pertenece al compilador grafo→IR, TSK-0193 (ver
 * cabecera del módulo).
 * @param {NodoOClave[]} nodos
 * @param {GrafoArcos} grafo
 * @returns {ViolacionGrafo[]}
 */
export function validarGrafoArcos(nodos, grafo) {
  if (!Array.isArray(nodos)) {
    throw new TypeError('"nodos" debe ser un array de NodoModelo | "inicio" | "fin"');
  }
  requerirGrafoArcos(grafo);

  /** @type {Map<string, NodoOClave>} */
  const nodosPorId = new Map();
  for (const entrada of nodos) {
    nodosPorId.set(idDe(entrada), entrada);
  }

  /** @type {ViolacionGrafo[]} */
  const violaciones = [];

  // 1) Compatibilidad de puertos, arco por arco.
  for (const arco of grafo.arcos) {
    const desdeEntrada = nodosPorId.get(arco.desdeNodoId);
    if (desdeEntrada === undefined) {
      registrar(violaciones, {
        tipo: 'NODO_DESCONOCIDO',
        nodoId: arco.desdeNodoId,
        arcoId: arco.id,
        mensaje: `el arco '${arco.id}' parte de un nodo desconocido '${arco.desdeNodoId}'`,
      });
    } else if (desdeEntrada === CLAVE_FIN) {
      registrar(violaciones, {
        tipo: 'FIN_CON_SALIDA',
        nodoId: CLAVE_FIN,
        arcoId: arco.id,
        mensaje: `Fin no admite arcos salientes (arco '${arco.id}')`,
      });
    } else if (!puertosDeNodo(desdeEntrada).salidas.includes(arco.desdePuerto)) {
      registrar(violaciones, {
        tipo: 'PUERTO_INCOMPATIBLE',
        nodoId: arco.desdeNodoId,
        arcoId: arco.id,
        mensaje: `el puerto de salida '${arco.desdePuerto}' no es válido para el nodo '${arco.desdeNodoId}'`,
      });
    }

    const hastaEntrada = nodosPorId.get(arco.hastaNodoId);
    if (hastaEntrada === undefined) {
      registrar(violaciones, {
        tipo: 'NODO_DESCONOCIDO',
        nodoId: arco.hastaNodoId,
        arcoId: arco.id,
        mensaje: `el arco '${arco.id}' llega a un nodo desconocido '${arco.hastaNodoId}'`,
      });
    } else if (hastaEntrada === CLAVE_INICIO) {
      registrar(violaciones, {
        tipo: 'INICIO_CON_ENTRADA',
        nodoId: CLAVE_INICIO,
        arcoId: arco.id,
        mensaje: `Inicio no admite arcos entrantes (arco '${arco.id}')`,
      });
    } else if (arco.hastaPuerto !== PUERTO.ENTRADA || !puertosDeNodo(hastaEntrada).entrada) {
      registrar(violaciones, {
        tipo: 'PUERTO_INCOMPATIBLE',
        nodoId: arco.hastaNodoId,
        arcoId: arco.id,
        mensaje: `el puerto de entrada '${arco.hastaPuerto}' no es válido para el nodo '${arco.hastaNodoId}'`,
      });
    }
  }

  // 2) Cardinalidad por nodo.
  for (const entrada of nodos) {
    const id = idDe(entrada);

    if (entrada === CLAVE_INICIO) {
      const salidaCount = arcosDesde(grafo, id, PUERTO.SALIDA).length;
      if (salidaCount === 0) {
        registrar(violaciones, {
          tipo: 'INICIO_SIN_SALIDA',
          nodoId: id,
          mensaje: 'Inicio debe tener exactamente un arco de salida',
        });
      } else if (salidaCount > 1) {
        registrar(violaciones, {
          tipo: 'MULTIPLES_SALIDAS',
          nodoId: id,
          mensaje: `Inicio tiene ${salidaCount} arcos de salida; debe tener exactamente 1`,
        });
      }
      continue;
    }

    if (entrada === CLAVE_FIN) {
      const entradaCount = arcosHacia(grafo, id, PUERTO.ENTRADA).length;
      if (entradaCount === 0) {
        registrar(violaciones, {
          tipo: 'FIN_SIN_ENTRADA',
          nodoId: id,
          mensaje: 'Fin debe tener exactamente un arco entrante',
        });
      } else if (entradaCount > 1) {
        registrar(violaciones, {
          tipo: 'MULTIPLES_ENTRADAS',
          nodoId: id,
          mensaje: `Fin tiene ${entradaCount} arcos entrantes; debe tener exactamente 1`,
        });
      }
      continue;
    }

    const tipoNodo = /** @type {NodoModelo} */ (entrada).nodo;
    const entradaCount = arcosHacia(grafo, id, PUERTO.ENTRADA).length;
    if (entradaCount > 1) {
      registrar(violaciones, {
        tipo: 'MULTIPLES_ENTRADAS',
        nodoId: id,
        mensaje: `el bloque '${id}' tiene ${entradaCount} arcos entrantes; como máximo 1`,
      });
    }

    if (tipoNodo === TIPO_NODO.CONDICIONAL) {
      const verdaderoCount = arcosDesde(grafo, id, PUERTO.VERDADERO).length;
      const falsoCount = arcosDesde(grafo, id, PUERTO.FALSO).length;
      if (verdaderoCount === 0) {
        registrar(violaciones, {
          tipo: 'CONDICIONAL_RAMA_FALTANTE',
          nodoId: id,
          mensaje: `falta el arco de la rama verdadero del condicional '${id}'`,
        });
      } else if (verdaderoCount > 1) {
        registrar(violaciones, {
          tipo: 'CONDICIONAL_RAMA_DUPLICADA',
          nodoId: id,
          mensaje: `el condicional '${id}' tiene ${verdaderoCount} arcos en la rama verdadero; debe tener exactamente 1`,
        });
      }
      if (falsoCount === 0) {
        registrar(violaciones, {
          tipo: 'CONDICIONAL_RAMA_FALTANTE',
          nodoId: id,
          mensaje: `falta el arco de la rama falso del condicional '${id}'`,
        });
      } else if (falsoCount > 1) {
        registrar(violaciones, {
          tipo: 'CONDICIONAL_RAMA_DUPLICADA',
          nodoId: id,
          mensaje: `el condicional '${id}' tiene ${falsoCount} arcos en la rama falso; debe tener exactamente 1`,
        });
      }
    } else if (NODOS_BUCLE.has(tipoNodo)) {
      const cuerpoCount = arcosDesde(grafo, id, PUERTO.CUERPO).length;
      const salidaCount = arcosDesde(grafo, id, PUERTO.SALIDA).length;
      if (cuerpoCount === 0) {
        registrar(violaciones, {
          tipo: 'BUCLE_SIN_CUERPO',
          nodoId: id,
          mensaje: `falta el arco del cuerpo del bucle '${id}'`,
        });
      } else if (cuerpoCount > 1) {
        registrar(violaciones, {
          tipo: 'BUCLE_CUERPO_DUPLICADO',
          nodoId: id,
          mensaje: `el bucle '${id}' tiene ${cuerpoCount} arcos de cuerpo; debe tener exactamente 1`,
        });
      }
      if (salidaCount > 1) {
        registrar(violaciones, {
          tipo: 'MULTIPLES_SALIDAS',
          nodoId: id,
          mensaje: `el bucle '${id}' tiene ${salidaCount} arcos de salida; como máximo 1`,
        });
      }
    } else if (tipoNodo !== TIPO_NODO.COMENTARIO) {
      const salidaCount = arcosDesde(grafo, id, PUERTO.SALIDA).length;
      if (salidaCount > 1) {
        registrar(violaciones, {
          tipo: 'MULTIPLES_SALIDAS',
          nodoId: id,
          mensaje: `el bloque '${id}' tiene ${salidaCount} arcos de salida; como máximo 1`,
        });
      }
    }
  }

  // 3) Aciclicidad: DFS con detección de arco de retorno (back-edge) sobre
  // TODOS los nodos conocidos (incluidas las pseudo-anclas), ignorando
  // arcos cuyo extremo ya se reportó como NODO_DESCONOCIDO.
  /** @type {Map<string, { destino: string, arcoId: string }[]>} */
  const adyacencia = new Map();
  for (const id of nodosPorId.keys()) adyacencia.set(id, []);
  for (const arco of grafo.arcos) {
    if (nodosPorId.has(arco.desdeNodoId) && nodosPorId.has(arco.hastaNodoId)) {
      /** @type {{ destino: string, arcoId: string }[]} */
      (adyacencia.get(arco.desdeNodoId)).push({ destino: arco.hastaNodoId, arcoId: arco.id });
    }
  }

  const SIN_VISITAR = 0;
  const EN_PROCESO = 1;
  const VISITADO = 2;
  /** @type {Map<string, number>} */
  const estados = new Map();
  for (const id of nodosPorId.keys()) estados.set(id, SIN_VISITAR);
  const arcosDeCicloYaReportados = new Set();

  /** @param {string} id */
  function visitar(id) {
    estados.set(id, EN_PROCESO);
    for (const { destino, arcoId } of adyacencia.get(id) ?? []) {
      const estadoDestino = estados.get(destino);
      if (estadoDestino === EN_PROCESO) {
        if (!arcosDeCicloYaReportados.has(arcoId)) {
          arcosDeCicloYaReportados.add(arcoId);
          registrar(violaciones, {
            tipo: 'CICLO',
            nodoId: destino,
            arcoId,
            mensaje:
              `el arco '${arcoId}' cierra un ciclo de vuelta a '${destino}'; el grafo dibujado ` +
              'debe ser acíclico (usa el puerto CUERPO del bucle para expresar la repetición)',
          });
        }
      } else if (estadoDestino === SIN_VISITAR) {
        visitar(destino);
      }
    }
    estados.set(id, VISITADO);
  }

  for (const id of nodosPorId.keys()) {
    if (estados.get(id) === SIN_VISITAR) visitar(id);
  }

  return violaciones;
}
